// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of "Stampa modulo doc", the print entry of a row of the dossier on the 3.0.0 archive
 * page (`documentsV3`): the data of the print dialog of the legacy dossier
 * (`PropertyDetailDocumentPrintModuleDialogComponent`), built out of ONE row.
 *
 * The dialog is reused as it is. It prints the module and the labels itself, and for a paper document
 * it asks the delivery note for the parts. What it does not survive is a missing date or a missing
 * address: both are dereferenced without a guard further down (`PrintQrDialogComponent` calls
 * `uploadDate.toString()`), so the builder always hands both over, and answers "nothing to print"
 * instead of letting the dialog break.
 *
 * ONLY THE CLICKED ROW, as the legacy dossier reprints a module (`onTogglePrintModuleDoc`): the type of
 * that row and the block it sits in, never the other places the same document is filed under. The
 * module printed right after an insert lists them all; this one does not, as in the legacy.
 *
 * @since 3.0.0
 */

/**
 * The address the print dialog reads. Every field is a string: the dialog prints them unguarded.
 *
 * @since 3.0.0
 */
export interface ArchivePrintAddress {
  city: string;
  route: string;
  streetNumber: string;
}

/**
 * The property as the print dialog reads it (`data.property`).
 *
 * @since 3.0.0
 */
export interface ArchivePrintProperty {
  name: string;
  address: ArchivePrintAddress;
}

/**
 * One document type of the module (`data.docs`).
 *
 * @since 3.0.0
 */
export interface ArchivePrintDoc {
  /** Printed in the list of the documents, and on the labels */
  title: string;
  /**
   * Dedup key of `PrintQrDialogComponent`, which keeps one document per key: the type id, never the
   * retired legacy key.
   */
  key: string;
  /** The section of the row, printed as "Sottoargomento" */
  subAreaTitle: string;
}

/**
 * The values of the document the print dialog reads, and nothing else.
 *
 * @since 3.0.0
 */
export interface ArchivePrintItemData {
  /** What the QR encodes. On a paper document the dialog replaces it with its parts, when it has any. */
  barCode: string;
  /** Printed as "Data di carico" @see archivePrintUploadDate */
  createdAt: string;
  /** Printed as the content of the document, empty when there is none */
  contenutoDocumento: string;
}

/**
 * The data of the print dialog of the legacy dossier, in the shape `onTogglePrintModuleDoc` gives it.
 *
 * @since 3.0.0
 */
export interface ArchivePrintModuleDialogData {
  /** Title of the dialog: the type of the row */
  modalTitle: string;
  itemData: ArchivePrintItemData;
  property: ArchivePrintProperty;
  /** Code of the area, "1" to "4": the dialog prints `properties.addDocumentsPage.area.<code>` */
  area: string;
  docs: ArchivePrintDoc[];
  /** The block of the row, printed as "Impianto"; empty when the row sits in none */
  structure: Array<{ name: string }>;
}

/**
 * What the builder reads of a row: the document values and the creation of the row itself.
 *
 * @since 3.0.0
 */
export interface ArchivePrintRow {
  data?: { [key: string]: unknown } | null;
  createdAt?: string;
}

/**
 * What the page knows about the row and around it, in the language of the page.
 *
 * @since 3.0.0
 */
export interface ArchivePrintModuleInput {
  row: ArchivePrintRow;
  /** `_id` of the document type: only the dedup key of the dialog */
  typeId: string;
  /** Name of the document type */
  typeLabel: string;
  /** Name of the section */
  sectionLabel: string;
  /** Code of the area, "1" to "4" */
  areaCode: string;
  /** Heading of the block the row sits in, empty when it sits in none */
  blockLabel: string;
  property: ArchivePrintProperty;
}

/** Whether a value is a plain object whose keys can be read */
function isRecord(value: unknown): value is { [key: string]: unknown } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A value as the dialog prints it: the text, or the empty string. Interpolated as it is, a missing
 * value would print "undefined"; a number is kept, a house number can be stored as one.
 */
function textOf(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }
  return "";
}

/**
 * Whether the dialog can read a value as a date. It runs `new Date(value.toString()).toISOString()`,
 * which throws on an invalid date, so this asks the same question first.
 */
function isPrintableDate(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && !isNaN(new Date(value).getTime());
}

/** The values of the document carried by a row, an empty object when it carries none */
function dataOf(row: ArchivePrintRow | null | undefined): { [key: string]: unknown } {
  return row && isRecord(row.data) ? row.data : {};
}

/**
 * The property as the print dialog reads it, out of the answer of `GET properties/:id`.
 *
 * Every field is a string, empty when the property does not carry it: the dialog composes the address
 * as "route, number" and prints the rest as it is. `null` when there is no property at all.
 *
 * @since 3.0.0
 *
 * @param property `data` of the answer
 */
export function archivePrintPropertyOf(property: unknown): ArchivePrintProperty | null {
  if (!isRecord(property)) {
    return null;
  }
  const address = isRecord(property.address) ? property.address : {};
  return {
    name: textOf(property.name),
    address: {
      city: textOf(address.city),
      route: textOf(address.route),
      streetNumber: textOf(address.streetNumber),
    },
  };
}

/**
 * The date the module prints as "Data di carico": when the document was filed.
 *
 * `data.createdAt` first: it records when the DOCUMENT was filed. The migrated rows carry it, and the
 * server stamps it on the documents it files. The row's own `createdAt` after it: a document filed
 * from this archive before the server stamped `data.createdAt` has only that one, and it is the same
 * moment.
 *
 * `null` when neither is a date: the dialog would throw on it, so the row is not offered for print.
 *
 * @since 3.0.0
 *
 * @param row The row to print
 */
export function archivePrintUploadDate(row: ArchivePrintRow | null | undefined): string | null {
  if (!row) {
    return null;
  }
  const createdAt = dataOf(row).createdAt;
  if (isPrintableDate(createdAt)) {
    return createdAt;
  }
  if (isPrintableDate(row.createdAt)) {
    return row.createdAt;
  }
  return null;
}

/**
 * Whether a row can be printed: a barcode for the QR to encode, and a date the dialog can read.
 *
 * Without a barcode the legacy dialog only answers "No qrCodes to inject", in English; without a date
 * it throws. The caller adds its own gates on top: the role, and a filed row.
 *
 * @since 3.0.0
 *
 * @param row The row to print
 */
export function isArchiveRowPrintable(row: ArchivePrintRow | null | undefined): boolean {
  return (
    !!row &&
    textOf(dataOf(row).barCode).trim() !== "" &&
    archivePrintUploadDate(row) !== null
  );
}

/**
 * The data of the print dialog for ONE row of the dossier.
 *
 * Only the values the dialog reads are handed over, in a new object: the row of the tree is never
 * passed, and never changed.
 *
 * `null` when the row cannot be printed (@see isArchiveRowPrintable) or when the property is missing.
 *
 * @since 3.0.0
 *
 * @param input The row, its type, section, area and block, and the property
 */
export function buildArchivePrintModuleData(
  input: ArchivePrintModuleInput
): ArchivePrintModuleDialogData | null {
  if (!input || !input.property || !isArchiveRowPrintable(input.row)) {
    return null;
  }
  const uploadDate = archivePrintUploadDate(input.row);
  if (uploadDate === null) {
    return null;
  }

  const data = dataOf(input.row);
  const typeLabel = textOf(input.typeLabel);
  const blockLabel = textOf(input.blockLabel);

  return {
    modalTitle: typeLabel,
    itemData: {
      barCode: textOf(data.barCode),
      createdAt: uploadDate,
      contenutoDocumento: textOf(data.contenutoDocumento),
    },
    property: {
      name: input.property.name,
      address: {
        city: input.property.address.city,
        route: input.property.address.route,
        streetNumber: input.property.address.streetNumber,
      },
    },
    area: textOf(input.areaCode),
    docs: [
      {
        title: typeLabel,
        key: textOf(input.typeId),
        subAreaTitle: textOf(input.sectionLabel),
      },
    ],
    structure: blockLabel ? [{ name: blockLabel }] : [],
  };
}
